/**
 * DigitalVerifyStep — Step 2 (verify) of the digital checkout variant.
 *
 * Renders:
 *   - Total-to-debit card with the redirect-to-order-detail explanation.
 *   - Inline error alert + footer (Atrás → Step 1 / Comprar y descargar →
 *     calls `callCreate`, anti-P-043 disables button while submitting).
 *
 * Founder directive (2026-11): the 2FA-verified banner was removed —
 * the verify step is now a plain confirmation step (no 2FA prerequisite).
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * Preserved VERBATIM — the original verify JSX block (lines 2546-2623 of
 * the monolith).
 */

import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import {
  Loader2,
  ArrowLeft,
  Download,
} from 'lucide-react';
import { formatCredits } from '../helpers';
import { SubmitErrorAlert } from './submit-error-alert';

// ─── Component ───────────────────────────────────────────────────────────────

export interface DigitalVerifyStepProps {
  grandTotal: number;
  submitting: boolean;
  submitError: string | null;
  onBack: () => void;
  onCreate: () => void;
}

export function DigitalVerifyStep({
  grandTotal,
  submitting,
  submitError,
  onBack,
  onCreate,
}: DigitalVerifyStepProps) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm">
        <div className="flex justify-between mb-2">
          <span className="text-muted-foreground">Total a debitar</span>
          <span className="text-lg font-bold tabular-nums">
            {formatCredits(grandTotal)} créditos
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Tras el pago, serás redirigido a la página de la orden donde
          encontrarás el botón de descarga.
        </p>
      </div>

      {submitError && <SubmitErrorAlert submitError={submitError} />}

      <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex-row gap-2 border-t border-border bg-background px-6 py-3 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="flex-1"
          disabled={submitting}
        >
          <ArrowLeft className="size-4 mr-1" aria-hidden="true" />
          Atrás
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={onCreate}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 mr-1 animate-spin" aria-hidden="true" />
              Procesando…
            </>
          ) : (
            <>
              <Download className="size-4 mr-1" aria-hidden="true" />
              Comprar y descargar
            </>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
}
